import React from "react";
import { useCustom, useList, useTranslate } from "@refinedev/core";
import { Col, Flex, Row } from "antd";
import dayjs from "dayjs";
import { EmptyState } from "@libertify/ui";
import { TrendUpIcon } from "@phosphor-icons/react";
import { PresetDateRangePicker } from "components/PresetDateRangePicker/";
import { ANALYTICS_MAX_DATE_RANGE_MONTHS } from "constants/";
import type {
  DocumentCompletionBreakdown,
  VideoCompletionBreakdown,
  VideoPositionPoint,
  VideoSummaryStats,
} from "types/";
import type { MediaProjectResponse } from "../../../types";
import type { AnalyticsDateRange } from "../../../projects/hooks/useAnalyticsDateRange";
import type { AnalyticsVersionState } from "../../../projects/hooks/useAnalyticsVersion";
import { useMediaTimings } from "../../../projects/hooks/useMediaTimings";
import { CompactStatCard } from "../CompactStatCard";
import { AllVersionsTag } from "../AllVersionsTag";
import { ViewerRetentionChart } from "../ViewerRetentionChart";
import { SeekPatternsChart } from "../SeekPatternsChart";
import { ViewerSegmentsChart } from "../ViewerSegmentsChart";
import { ComparisonCard } from "../ComparisonCard";
import { PlayFunnelCard } from "../PlayFunnelCard";
import { SpeedDistributionChart } from "../SpeedDistributionChart";
import { ANALYTICS_STALE_TIME, TARGETS } from "../constants/constants";
import {
  buildDateFilters,
  buildVersionFilters,
  showsOneVersion,
} from "../utils/";

const T_PREFIX = "media.projects.analytics.videoExplainer";

export type VideoPanelProps = {
  projectId: string | undefined;
  project: MediaProjectResponse | undefined;
  dateRange: AnalyticsDateRange;
  setDateRange: (range: AnalyticsDateRange) => void;
  version: AnalyticsVersionState | undefined;
  /** False when the page around the panel already offers the range. */
  showDatePicker?: boolean;
};

/** Video analytics for the picked date range and version. */
export const VideoPanel: React.FC<VideoPanelProps> = ({
  projectId,
  project,
  dateRange,
  setDateRange,
  version,
  showDatePicker = true,
}) => {
  const t = useTranslate();
  const mediaId = project?.media?.[0]?.id;
  const { chapters, durationSeconds } = useMediaTimings(mediaId);

  // Video length used as the charts' x-axis end: from chapter timings, or the
  // project's configured duration when timings aren't available.
  const videoDurationSeconds = durationSeconds ?? project?.settings.duration;

  const dateFilters = buildDateFilters(dateRange);
  const versionFilters = buildVersionFilters(version?.requested);
  // These endpoints take no version: say so while one version is shown.
  const oneVersion = showsOneVersion(version);
  const allVersionsTag = <AllVersionsTag hidden={!oneVersion} />;
  const documentTag = <AllVersionsTag part="document" hidden={!oneVersion} />;
  const dateEnabled =
    !!projectId && !!dateRange.startDate && !!dateRange.endDate;

  // Same request as the standalone video page's summary, so both share it.
  const {
    data: summary,
    isLoading: isSummaryLoading,
    isFetching: isSummaryFetching,
    isSuccess,
    isError,
  } = useCustom<VideoSummaryStats>({
    url: `analytics/projects/${projectId}/analytics/video/summary`,
    method: "get",
    config: { filters: [...dateFilters, ...versionFilters] },
    queryOptions: { enabled: dateEnabled, staleTime: ANALYTICS_STALE_TIME },
    errorNotification: false,
  });
  const videoData = summary?.data ?? undefined;
  const isDataLoading = isSummaryLoading || isSummaryFetching;

  const {
    data: positionAnalysis,
    isLoading: isPositionLoading,
    isFetching: isPositionFetching,
  } = useList<VideoPositionPoint>({
    resource: `analytics/projects/${projectId}/analytics/video/position-analysis`,
    config: { filters: [...dateFilters, ...versionFilters] },
    queryOptions: { enabled: dateEnabled, staleTime: ANALYTICS_STALE_TIME },
    errorNotification: false,
  });

  const {
    data: videoCompletion,
    isLoading: isVideoCompletionLoading,
    isFetching: isVideoCompletionFetching,
  } = useList<VideoCompletionBreakdown>({
    resource: `analytics/projects/${projectId}/analytics/video/completion`,
    config: { filters: [...dateFilters, ...versionFilters] },
    queryOptions: { enabled: dateEnabled, staleTime: ANALYTICS_STALE_TIME },
    errorNotification: false,
  });

  const {
    data: docCompletion,
    isLoading: isDocCompletionLoading,
    isFetching: isDocCompletionFetching,
  } = useList<DocumentCompletionBreakdown>({
    resource: `analytics/projects/${projectId}/analytics/document/completion`,
    config: { filters: dateFilters },
    queryOptions: { enabled: dateEnabled, staleTime: ANALYTICS_STALE_TIME },
    errorNotification: false,
  });

  const positionData = positionAnalysis?.data;
  const isPositionDataLoading = isPositionLoading || isPositionFetching;
  const videoCompletionData =
    videoCompletion?.data as unknown as VideoCompletionBreakdown;
  const docCompletionData =
    docCompletion?.data as unknown as DocumentCompletionBreakdown;
  const isCompletionLoading =
    isVideoCompletionLoading ||
    isVideoCompletionFetching ||
    isDocCompletionLoading ||
    isDocCompletionFetching;
  const noData = (isSuccess || isError) && !videoData && !isDataLoading;

  return (
    <Flex vertical gap={24} style={{ width: "100%" }}>
      {showDatePicker ? (
        <PresetDateRangePicker
          value={dateRange}
          onChange={setDateRange}
          dateFormat="L"
          minDate={dayjs().subtract(ANALYTICS_MAX_DATE_RANGE_MONTHS, "months")}
          disabled={isDataLoading}
        />
      ) : null}

      {noData ? (
        // Same empty state as the other analytics tabs.
        <Flex justify="center" align="center" style={{ marginTop: 100 }}>
          <EmptyState
            icon={<TrendUpIcon size={80} />}
            title={t("media.projects.analytics.dashboard.noData")}
            description={t("media.projects.analytics.dashboard.noDataHint")}
          />
        </Flex>
      ) : (
        <>
          <Row gutter={[8, 8]} style={{ width: "100%" }}>
            <Col sm={24} md={8}>
              <CompactStatCard
                label={t(`${T_PREFIX}.completion`)}
                metric={videoData?.avg_completion_pct}
                target={TARGETS.videoExplainer.avg_completion_pct}
                aim="high"
                type="percentage"
                loading={isDataLoading}
                tooltip={t(`${T_PREFIX}.completionTooltip`)}
              />
            </Col>
            <Col sm={24} md={8}>
              <CompactStatCard
                label={t(`${T_PREFIX}.totalSessions`)}
                metric={videoData?.impression_sessions}
                loading={isDataLoading}
                tooltip={t(`${T_PREFIX}.totalSessionsTooltip`)}
              />
            </Col>
            <Col sm={24} md={8}>
              <CompactStatCard
                label={t(`${T_PREFIX}.medianDropOff`)}
                metric={videoData?.median_drop_seconds}
                target={TARGETS.videoExplainer.median_drop_seconds}
                aim="high"
                type="seconds"
                loading={isDataLoading}
                tooltip={t(`${T_PREFIX}.medianDropOffTooltip`)}
              />
            </Col>
          </Row>

          <Row gutter={[8, 8]} style={{ width: "100%" }}>
            <Col sm={24} md={8}>
              <CompactStatCard
                label={t(`${T_PREFIX}.fullCompleters`)}
                metric={videoData?.full_completers_pct}
                target={TARGETS.videoExplainer.full_completers_pct}
                aim="high"
                type="percentage"
                loading={isDataLoading}
                tooltip={t(`${T_PREFIX}.fullCompletersTooltip`)}
              />
            </Col>
            <Col sm={24} md={8}>
              <CompactStatCard
                label={t(`${T_PREFIX}.playRate`)}
                metric={videoData?.play_rate_pct}
                target={TARGETS.videoExplainer.play_rate}
                aim="high"
                type="percentage"
                loading={isDataLoading}
                tooltip={t(`${T_PREFIX}.playRateTooltip`)}
              />
            </Col>
            <Col sm={24} md={8}>
              <CompactStatCard
                label={t(`${T_PREFIX}.chatRate`)}
                metric={videoData?.chat_rate_pct}
                target={TARGETS.videoExplainer.chat_rate_pct}
                aim="high"
                type="percentage"
                loading={isDataLoading}
                tooltip={t(`${T_PREFIX}.chatRateTooltip`)}
              />
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ width: "100%" }}>
            <Col sm={24} md={12}>
              <ViewerRetentionChart
                data={positionData}
                chapters={chapters}
                maxSeconds={videoDurationSeconds}
                loading={isPositionDataLoading}
              />
            </Col>
            <Col sm={24} md={12}>
              <SeekPatternsChart
                data={positionData}
                chapters={chapters}
                maxSeconds={videoDurationSeconds}
                loading={isPositionDataLoading}
              />
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ width: "100%" }}>
            <Col sm={24} md={8}>
              <ComparisonCard
                badge={documentTag}
                title={t(
                  "media.projects.analytics.dashboard.summaryCards.completionByTime.title"
                )}
                videoCompletionData={videoCompletionData}
                docCompletionData={docCompletionData}
                breakdownKey="by_time"
                loading={isCompletionLoading}
              />
            </Col>
            <Col sm={24} md={8}>
              <ComparisonCard
                badge={documentTag}
                title={t(
                  "media.projects.analytics.dashboard.summaryCards.completionByDevice.title"
                )}
                videoCompletionData={videoCompletionData}
                docCompletionData={docCompletionData}
                breakdownKey="by_device"
                loading={isCompletionLoading}
              />
            </Col>
            <Col sm={24} md={8}>
              <ComparisonCard
                badge={documentTag}
                title={t(
                  "media.projects.analytics.dashboard.summaryCards.completionBySource.title"
                )}
                videoCompletionData={videoCompletionData}
                docCompletionData={docCompletionData}
                breakdownKey="by_source"
                loading={isCompletionLoading}
              />
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ width: "100%" }}>
            <Col sm={24} md={8}>
              <ViewerSegmentsChart
                projectId={projectId}
                dateRange={dateRange}
                badge={allVersionsTag}
              />
            </Col>
            <Col sm={24} md={8}>
              <PlayFunnelCard
                data={{
                  impressionSessions: videoData?.impression_sessions ?? 0,
                  playSessions: videoData?.play_sessions ?? 0,
                  playRate: videoData?.play_rate_pct ?? 0,
                }}
                loading={isDataLoading}
              />
            </Col>
            <Col sm={24} md={8}>
              <SpeedDistributionChart
                projectId={projectId}
                badge={allVersionsTag}
              />
            </Col>
          </Row>
        </>
      )}
    </Flex>
  );
};
